/**
 * `navigator.audioSession.type = 'playback'`, feature-detected.
 *
 * No iPad since the Air 2 has a physical mute switch — muting is a Control Center toggle — and
 * Safari 16.4+ exposes `audioSession`, where `'playback'` makes Web Audio ignore silent mode.
 *
 * This one line carries more weight than its size suggests. **Every** utterance routes through
 * Web Audio: cached phrases, Azure, and Piper later. So if silent mode can suppress Web Audio,
 * the entire speech function is unreliable rather than merely inconvenient — a person could be
 * tapping "Ich habe Schmerzen" into a device that makes no sound and gives no sign of it.
 *
 * Phase 0 never ran the silent-mode probe (only a feature-detect was logged), so this is
 * build-and-verify: set it here, then confirm audibility with mute on during the device
 * session. `docs/device-checklist-phase-1.md` carries that check, and it is marked as one only
 * the device can answer.
 */

interface AudioSessionCapableNavigator extends Navigator {
  audioSession?: { type: string };
}

export type AudioSessionResult = 'set' | 'unsupported' | 'failed';

export function configureAudioSession(): AudioSessionResult {
  if (typeof navigator === 'undefined') return 'unsupported';

  const session = (navigator as AudioSessionCapableNavigator).audioSession;
  if (!session) {
    // Not fatal: playback may still be audible. It does mean silent mode is untamed, so it is
    // worth surfacing in diagnostics rather than passing over.
    console.warn('[audio] navigator.audioSession unsupported — silent mode may suppress speech');
    return 'unsupported';
  }

  try {
    session.type = 'playback';
    console.info('[audio] audioSession.type = playback');
    return 'set';
  } catch (err) {
    console.error('[audio] could not set audioSession.type', err);
    return 'failed';
  }
}
